import React, { useState, useRef, useEffect } from 'react';
import { 
  Send, 
  RefreshCw, 
  ExternalLink, 
  FileCode, 
  CheckCircle2, 
  Loader2, 
  AlertCircle,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  Eye,
  Code2,
  Sparkles,
  History,
  RotateCcw,
  GitCommit,
  Clock,
  GitPullRequest,
  GitBranch,
  GitMerge,
  Plus,
  FolderSync,
  Wand2,
  Settings,
  Trash2,
  Package,
  Database,
  HardDrive
} from 'lucide-react';
import { ScrollArea } from './ui/scroll-area';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { useSiteAgent } from '../hooks/useSiteAgent';
import { useGitHistory } from '../hooks/useGitHistory';
import { useBrandSandbox, SANDBOX_STATUS, DEV_SERVER_STATUS } from '../hooks/useBrandSandbox';
import { useTheme } from '../contexts/ThemeContext';
import { cn } from '../lib/utils';
import { PendingChangesPanel } from './DiffViewer';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3000';

/**
 * StagedFileRow - Expandable row showing a staged file with diff preview
 */
const StagedFileRow = ({ file, brandId, isBrandTheme, isDark, surfaceBg, textSecondary, textMuted, onCommitFile }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [diffData, setDiffData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [committing, setCommitting] = useState(false);

  const fetchDiff = async () => {
    if (diffData || loading) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(
        `${API_BASE}/api/brands/${brandId}/sandbox/git?action=file-diff&path=${encodeURIComponent(file.path)}`,
        { credentials: 'include' }
      );
      
      if (!response.ok) {
        throw new Error(`Failed to fetch diff: ${response.status}`);
      }
      
      const data = await response.json();
      setDiffData(data);
    } catch (err) {
      console.error('Failed to fetch diff:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = () => {
    if (!isExpanded) {
      fetchDiff();
    }
    setIsExpanded(!isExpanded);
  };

  const handleCommitFile = async (e) => {
    e.stopPropagation();
    if (!onCommitFile || committing) return;
    
    setCommitting(true);
    try {
      await onCommitFile(file.path, `Commit ${file.path}`);
    } finally {
      setCommitting(false);
    }
  };

  return (
    <div className={cn('rounded border', isBrandTheme ? 'border-white/10' : isDark ? 'border-gray-700' : 'border-gray-200')}>
      {/* Header row */}
      <div
        className={cn(
          'flex items-center gap-2 px-3 py-2 text-sm',
          surfaceBg
        )}
      >
        <button
          onClick={handleToggle}
          className="flex items-center gap-2 flex-1 text-left hover:opacity-80 transition-opacity"
        >
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 flex-shrink-0" />
          ) : (
            <ChevronRight className="w-4 h-4 flex-shrink-0" />
          )}
          <Badge 
            variant="outline" 
            className="text-xs border-amber-500/30 text-amber-500"
          >
            {file.action}
          </Badge>
          <code className={cn('text-xs truncate flex-1', textSecondary)}>
            {file.path}
          </code>
          {file.bytes && (
            <span className={cn('text-xs', textMuted)}>
              {file.bytes} bytes
            </span>
          )}
        </button>
        
        {/* Per-file commit button */}
        {onCommitFile && (
          <button
            onClick={handleCommitFile}
            disabled={committing}
            className={cn(
              'flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors',
              'bg-green-600/20 text-green-500 hover:bg-green-600/30',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
            title={`Commit ${file.path}`}
          >
            {committing ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <CheckCircle2 className="w-3 h-3" />
            )}
            Commit
          </button>
        )}
      </div>
      
      {/* Expanded diff content */}
      {isExpanded && (
        <div className={cn('border-t px-3 py-2', isBrandTheme ? 'border-white/10 bg-black/20' : isDark ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-gray-50')}>
          {loading && (
            <div className="flex items-center gap-2 py-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className={cn('text-xs', textMuted)}>Loading diff...</span>
            </div>
          )}
          
          {error && (
            <div className={cn('text-xs py-2', 'text-red-500')}>
              Error: {error}
            </div>
          )}
          
          {diffData && !loading && (
            <div className="space-y-2">
              {diffData.diff ? (
                <pre className={cn(
                  'text-xs overflow-x-auto p-2 rounded max-h-64 overflow-y-auto',
                  isBrandTheme ? 'bg-black/30' : isDark ? 'bg-gray-800' : 'bg-white border'
                )}>
                  {diffData.diff.split('\n').map((line, i) => {
                    let lineClass = textSecondary;
                    if (line.startsWith('+') && !line.startsWith('+++')) {
                      lineClass = 'text-green-500';
                    } else if (line.startsWith('-') && !line.startsWith('---')) {
                      lineClass = 'text-red-500';
                    } else if (line.startsWith('@@')) {
                      lineClass = 'text-cyan-500';
                    }
                    return (
                      <div key={i} className={lineClass}>
                        {line || ' '}
                      </div>
                    );
                  })}
                </pre>
              ) : (
                <p className={cn('text-xs py-2', textMuted)}>
                  No visible changes (file may be newly created or binary)
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * SiteAgentView - A three-panel layout for interacting with the site generation agent
 * 
 * Props:
 * - brandId: UUID of the brand (for per-brand sandbox)
 * - brandSlug: URL-safe slug of the brand
 * - brandName: Display name of the brand
 * - brandVisualTheme: Theme configuration
 * - onBack: Callback to navigate back
 * 
 * Layout:
 * ┌─────────────────────┬────────────────────────────────────────┐
 * │   Agent Chat        │        Live Preview                    │
 * │   ┌─────────────┐   │   ┌──────────────────────────────────┐ │
 * │   │ Steps Log   │   │   │                                  │ │
 * │   │             │   │   │   <iframe src="localhost">       │ │
 * │   │             │   │   │                                  │ │
 * │   └─────────────┘   │   └──────────────────────────────────┘ │
 * │   [Input...]        │   Changed Files / History              │
 * └─────────────────────┴────────────────────────────────────────┘
 */
const SiteAgentView = ({ brandId, brandSlug, brandName, brandVisualTheme, onBack }) => {
  const [prompt, setPrompt] = useState('');
  const [previewKey, setPreviewKey] = useState(0);
  const [showChangedFiles, setShowChangedFiles] = useState(true);
  const [showHistory, setShowHistory] = useState(false);
  const [activeTab, setActiveTab] = useState('pending'); // 'pending' | 'changes' | 'history'
  const [showSandboxPanel, setShowSandboxPanel] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Resizable panel state
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    // Load from localStorage or default to 400px
    const saved = localStorage.getItem('site-agent-sidebar-width');
    return saved ? parseInt(saved, 10) : 400;
  });
  const [consolePanelHeight, setConsolePanelHeight] = useState(() => {
    // Load from localStorage or default to 250px
    const saved = localStorage.getItem('site-agent-console-height');
    return saved ? parseInt(saved, 10) : 250;
  });
  const [isResizingSidebar, setIsResizingSidebar] = useState(false);
  const [isResizingConsole, setIsResizingConsole] = useState(false);
  const containerRef = useRef(null);

  // Persist sidebar width to localStorage
  useEffect(() => {
    localStorage.setItem('site-agent-sidebar-width', sidebarWidth.toString());
  }, [sidebarWidth]);

  // Persist console height to localStorage
  useEffect(() => {
    localStorage.setItem('site-agent-console-height', consolePanelHeight.toString());
  }, [consolePanelHeight]);

  // Handle sidebar resize
  useEffect(() => {
    if (!isResizingSidebar) return;

    const handleMouseMove = (e) => {
      const containerRect = containerRef.current?.getBoundingClientRect();
      if (!containerRect) return;
      
      const newWidth = e.clientX - containerRect.left;
      // Clamp between 280px and 600px
      setSidebarWidth(Math.max(280, Math.min(600, newWidth)));
    };

    const handleMouseUp = () => {
      setIsResizingSidebar(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizingSidebar]);

  // Handle console resize
  useEffect(() => {
    if (!isResizingConsole) return;

    const handleMouseMove = (e) => {
      const containerRect = containerRef.current?.getBoundingClientRect();
      if (!containerRect) return;
      
      // Calculate from bottom of container
      const newHeight = containerRect.bottom - e.clientY;
      // Clamp between 100px and 500px
      setConsolePanelHeight(Math.max(100, Math.min(500, newHeight)));
    };

    const handleMouseUp = () => {
      setIsResizingConsole(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'row-resize';
    document.body.style.userSelect = 'none';

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizingConsole]);

  const { resolvedTheme } = useTheme();
  const isBrandTheme = resolvedTheme === 'noderiver';
  const isDark = resolvedTheme === 'dark';
  const themeMode = brandVisualTheme?.modes?.[resolvedTheme] ?? brandVisualTheme?.modes?.light;

  // Sandbox management hook
  const {
    status: sandboxStatus,
    sandboxInfo,
    error: sandboxError,
    isReady: sandboxReady,
    isLoading: sandboxLoading,
    needsInit: sandboxNeedsInit,
    needsSync: sandboxNeedsSync,
    previewUrl,
    syncSpecs,
    regenerate,
    resetSandbox,
    // Dev server
    devServerStatus,
    devServerUrl,
    devServerRunning,
    devServerStarting,
    devServerError,
    startDevServer,
    stopDevServer,
    // Branch management
    currentBranch,
    branches,
    branchLoading,
    stashes,
    hasStashes,
    fetchBranches,
    createBranch,
    switchBranch,
    mergeBranch,
    deleteBranch,
    compareBranches,
    pullFromBranch,
    rebaseBranch,
    cherryPick,
    getBranchCommits,
    popStash,
    dropStash,
    applyStash,
  } = useBrandSandbox({ brandId, brandSlug, autoInit: true, autoStartDevServer: true });

  // Branch UI state
  const [showBranchMenu, setShowBranchMenu] = useState(false);
  const [newBranchName, setNewBranchName] = useState('');
  const [branchError, setBranchError] = useState(null);
  const [showBranchCompare, setShowBranchCompare] = useState(false);
  const [compareResult, setCompareResult] = useState(null);
  const [selectedBranchForCompare, setSelectedBranchForCompare] = useState(null);

  // Fetch branches on mount
  useEffect(() => {
    if (sandboxReady && brandId) {
      fetchBranches();
    }
  }, [sandboxReady, brandId, fetchBranches]);

  const {
    status,
    steps,
    changedFiles,
    stagedFiles,
    pendingChanges,
    pendingClarification,
    messages,
    error,
    currentThought,
    runAgent,
    reset,
    addMessage,
    respondToClarification,
    acceptChange,
    rejectChange,
    acceptAll,
    rejectAll,
    commitAll,
    commitFile,
    discardAll,
    pendingCount,
    stagedCount,
    isRunning,
    isDone,
    isAwaitingClarification,
    hasPendingChanges,
    hasStagedChanges,
    hasError,
    hasPendingClarification,
  } = useSiteAgent({ brandId, brandSlug });

  const {
    commits,
    loading: historyLoading,
    error: historyError,
    fetchHistory,
    revertCommit,
  } = useGitHistory({ brandId, brandSlug });

  // Get the preview URL - prefer dev server, then sandbox previewUrl, then static fallback
  const effectivePreviewUrl = devServerUrl || previewUrl || `${API_BASE}/api/brands/${brandId}/preview`;
  
  // Debug: log which URL is being used
  useEffect(() => {
    console.log('[Preview] URL sources:', { 
      devServerUrl, 
      previewUrl, 
      effectivePreviewUrl,
      devServerRunning,
      devServerStatus 
    });
  }, [devServerUrl, previewUrl, effectivePreviewUrl, devServerRunning, devServerStatus]);

  // Auto-scroll to bottom when steps update
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [steps, currentThought]);

  // Track the number of changed/staged files to detect new changes
  const [lastFileCount, setLastFileCount] = useState(0);
  const [lastStagedCount, setLastStagedCount] = useState(0);
  
  // Refresh preview when files change - trigger on each new file
  useEffect(() => {
    if (changedFiles.length > lastFileCount) {
      // New file committed - refresh immediately
      console.log(`[Preview] File committed: ${changedFiles[changedFiles.length - 1]?.path}`);
      setLastFileCount(changedFiles.length);
      setPreviewKey(k => k + 1);
    }
  }, [changedFiles, lastFileCount]);

  // Refresh preview when staged files change - this is the key for live preview!
  useEffect(() => {
    if (stagedFiles && stagedFiles.length > lastStagedCount) {
      // New file staged (written but not committed) - refresh immediately
      console.log(`[Preview] File staged: ${stagedFiles[stagedFiles.length - 1]?.path}`);
      setLastStagedCount(stagedFiles.length);
      // Refresh preview so user sees the changes
      setPreviewKey(k => k + 1);
    }
  }, [stagedFiles, lastStagedCount]);

  // Also refresh when agent completes successfully
  useEffect(() => {
    if (isDone && !hasError && changedFiles.length > 0) {
      // Final refresh after all changes
      const timer = setTimeout(() => {
        console.log('[Preview] Agent done - final refresh');
        setPreviewKey(k => k + 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isDone, hasError, changedFiles.length]);

  // Fetch history when tab changes to history
  useEffect(() => {
    if (activeTab === 'history') {
      fetchHistory(20);
    }
  }, [activeTab, fetchHistory]);

  // Refresh history after agent completes
  useEffect(() => {
    if (isDone && activeTab === 'history') {
      fetchHistory(20);
    }
  }, [isDone, activeTab, fetchHistory]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!prompt.trim() || isRunning) return;
    
    // If awaiting clarification, respond to it
    if (isAwaitingClarification && hasPendingClarification) {
      respondToClarification(prompt.trim());
      setPrompt('');
      return;
    }
    
    // Add user message to conversation (stored in hook with localStorage persistence)
    addMessage(prompt.trim());
    
    runAgent(prompt.trim());
    setPrompt('');
  };

  const handleRefreshPreview = () => {
    setPreviewKey(k => k + 1);
  };

  const handleRevert = async (commitHash) => {
    const result = await revertCommit(commitHash);
    if (result?.success) {
      // Refresh the preview after revert
      setTimeout(() => setPreviewKey(k => k + 1), 500);
    }
  };

  // Theme-aware styling
  const gradientBg = brandVisualTheme?.gradients?.hero || 'linear-gradient(135deg, #0B2433, #03131d)';
  const accentGradient = themeMode?.ctaGradient ?? brandVisualTheme?.gradients?.cta ?? 'linear-gradient(135deg, #1E8997, #0e273a)';

  // Shell background
  const shellStyle = isBrandTheme
    ? { background: gradientBg }
    : { backgroundColor: isDark ? '#0f172a' : '#ffffff' };

  // Border color
  const borderColor = isBrandTheme 
    ? 'border-white/10' 
    : isDark ? 'border-gray-800' : 'border-gray-200';

  // Text colors
  const textPrimary = isBrandTheme 
    ? 'text-white' 
    : isDark ? 'text-white' : 'text-gray-900';
  const textSecondary = isBrandTheme 
    ? 'text-white/60' 
    : isDark ? 'text-gray-400' : 'text-gray-500';
  const textMuted = isBrandTheme 
    ? 'text-white/40' 
    : isDark ? 'text-gray-500' : 'text-gray-400';

  // Surface colors
  const surfaceBg = isBrandTheme 
    ? 'bg-white/5' 
    : isDark ? 'bg-gray-800/50' : 'bg-gray-50';
  const surfaceHover = isBrandTheme 
    ? 'hover:bg-white/10' 
    : isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-100';

  // Input styling
  const inputBg = isBrandTheme 
    ? 'bg-white/5 border-white/10' 
    : isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200';
  const inputText = isBrandTheme 
    ? 'text-white placeholder:text-white/40' 
    : isDark ? 'text-white placeholder:text-gray-500' : 'text-gray-900 placeholder:text-gray-400';

  // Card styling
  const cardBg = isBrandTheme 
    ? 'bg-white/5 border-white/10' 
    : isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200';

  // Tab styling
  const tabActive = isBrandTheme
    ? 'bg-white/10 text-white'
    : isDark ? 'bg-gray-700 text-white' : 'bg-gray-200 text-gray-900';
  const tabInactive = isBrandTheme
    ? 'text-white/60 hover:text-white hover:bg-white/5'
    : isDark ? 'text-gray-400 hover:text-white hover:bg-gray-800' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100';

  return (
    <div className="flex h-screen" style={shellStyle} ref={containerRef}>
      {/* Left Panel - Agent Chat */}
      <div 
        className={cn('flex flex-col border-r flex-shrink-0', borderColor)}
        style={{ width: sidebarWidth }}
      >
        {/* Header */}
        <div className={cn('px-4 py-3 border-b', borderColor, surfaceBg)}>
          <div className="flex items-center gap-2">
            {onBack && (
              <button
                onClick={onBack}
                className={cn(
                  'p-1.5 rounded-lg transition-colors',
                  textSecondary,
                  surfaceHover,
                  isBrandTheme ? 'hover:text-white' : isDark ? 'hover:text-white' : 'hover:text-gray-900'
                )}
                title="Back to chat"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
            )}
            <div 
              className="w-8 h-8 rounded-full flex items-center justify-center"
              style={{ background: accentGradient }}
            >
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <div>
              <h2 className={cn('text-sm font-semibold', textPrimary)}>
                {brandName ? `${brandName} Site Agent` : 'Site Agent'}
              </h2>
              <p className={cn('text-xs', textSecondary)}>
                {isRunning ? 'Working...' : isDone ? 'Ready' : brandSlug ? `Sandbox: ${brandSlug}` : 'Ask me to modify the site'}
              </p>
            </div>
          </div>
        </div>

        {/* Conversation Log */}
        <ScrollArea className="flex-1 px-4 py-3" ref={scrollRef}>
          <div className="space-y-3">
            {messages.length === 0 && steps.length === 0 && !isRunning && (
              <div className="text-center py-8">
                <Code2 className={cn('w-12 h-12 mx-auto mb-3', textMuted)} />
                <p className={cn('text-sm', textMuted)}>
                  Tell me what to change on the site
                </p>
                <p className={cn('text-xs mt-1', textMuted)}>
                  e.g., "Change the hero title to Welcome"
                </p>
              </div>
            )}

            {/* Create unified timeline of messages and steps */}
            {(() => {
              // Merge messages and steps into a unified timeline
              const timeline = [
                ...messages.map((msg, idx) => ({
                  type: 'message',
                  data: msg,
                  timestamp: msg.timestamp || 0,
                  key: `msg-${idx}`,
                })),
                ...steps.map((step, idx) => ({
                  type: 'step',
                  data: step,
                  timestamp: step.timestamp || 0,
                  key: `step-${idx}`,
                })),
              ].sort((a, b) => a.timestamp - b.timestamp);

              return timeline.map((item) => {
                if (item.type === 'message') {
                  const msg = item.data;
                  return (
                    <React.Fragment key={item.key}>
                      {/* User message */}
                      {msg.role === 'user' && (
                        <div className={cn('flex justify-end')}>
                          <div className={cn(
                            'max-w-[85%] px-4 py-2 rounded-2xl rounded-tr-sm',
                            isBrandTheme 
                              ? 'bg-white/10 text-white' 
                              : isDark ? 'bg-cyan-900/30 text-cyan-100' : 'bg-cyan-100 text-cyan-900'
                          )}>
                            <p className="text-sm">{msg.content}</p>
                          </div>
                        </div>
                      )}
                      {/* Assistant message */}
                      {msg.role === 'assistant' && (
                        <div className={cn('flex justify-start')}>
                          <div className={cn(
                            'max-w-[85%] px-4 py-2 rounded-2xl rounded-tl-sm',
                            msg.type === 'clarification'
                              ? (isBrandTheme 
                                  ? 'bg-amber-500/20 text-white border border-amber-500/30' 
                                  : isDark ? 'bg-amber-900/30 text-amber-100 border border-amber-500/30' : 'bg-amber-50 text-amber-900 border border-amber-200')
                              : (isBrandTheme 
                                  ? 'bg-cyan-500/20 text-white' 
                                  : isDark ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-900')
                          )}>
                            {msg.type === 'clarification' && (
                              <div className="flex items-center gap-2 mb-1">
                                <Sparkles className="w-4 h-4 text-amber-500" />
                                <span className={cn('text-xs font-medium', isBrandTheme ? 'text-amber-300' : 'text-amber-600')}>
                                  Quick question
                                </span>
                              </div>
                            )}
                            <p className="text-sm">{msg.content}</p>
                            {/* Quick-reply options for clarification */}
                            {msg.type === 'clarification' && msg.options && msg.options.length > 0 && (
                              <div className="flex flex-wrap gap-2 mt-2">
                                {msg.options.map((option, optIdx) => (
                                  <Button
                                    key={optIdx}
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      respondToClarification(option);
                                    }}
                                    className={cn(
                                      'text-xs',
                                      isBrandTheme 
                                        ? 'border-amber-500/50 text-amber-300 hover:bg-amber-500/20' 
                                        : 'border-amber-300 text-amber-700 hover:bg-amber-50'
                                    )}
                                  >
                                    {option}
                                  </Button>
                                ))}
                              </div>
                            )}
                            {/* Default suggestion */}
                            {msg.type === 'clarification' && msg.default && !msg.options && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => respondToClarification(msg.default)}
                                className={cn(
                                  'mt-2 text-xs',
                                  isBrandTheme 
                                    ? 'border-amber-500/50 text-amber-300 hover:bg-amber-500/20' 
                                    : 'border-amber-300 text-amber-700 hover:bg-amber-50'
                                )}
                              >
                                Use: {msg.default}
                              </Button>
                            )}
                            {msg.totalSteps && (
                              <p className={cn('text-xs mt-1', textMuted)}>
                                Completed in {msg.totalSteps} steps
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </React.Fragment>
                  );
                } else if (item.type === 'step') {
                  return (
                    <StepCard 
                      key={item.key} 
                      step={item.data} 
                      isBrandTheme={isBrandTheme}
                      isDark={isDark}
                      cardBg={cardBg}
                      textPrimary={textPrimary}
                      textSecondary={textSecondary}
                      textMuted={textMuted}
                      borderColor={borderColor}
                    />
                  );
                }
                return null;
              });
            })()}

            {/* Current thinking indicator with streaming */}
            {isRunning && !isAwaitingClarification && currentThought && (
              <div className={cn(
                'flex items-start gap-3 p-3 rounded-lg',
                isBrandTheme ? 'bg-white/5' : isDark ? 'bg-gray-800/50' : 'bg-gray-50'
              )}>
                <div className="flex-shrink-0">
                  <Loader2 className="w-5 h-5 text-cyan-500 animate-spin" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={cn('text-sm', textSecondary)}>{currentThought}</p>
                  <div className="flex gap-1 mt-2">
                    <span className="w-2 h-2 bg-cyan-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 bg-cyan-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 bg-cyan-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}

            {/* Awaiting clarification indicator */}
            {isAwaitingClarification && (
              <div className={cn(
                'flex items-start gap-3 p-3 rounded-lg border',
                isBrandTheme ? 'bg-amber-500/10 border-amber-500/30' : isDark ? 'bg-amber-900/20 border-amber-700' : 'bg-amber-50 border-amber-200'
              )}>
                <div className="flex-shrink-0">
                  <Sparkles className="w-5 h-5 text-amber-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={cn('text-sm font-medium', isBrandTheme ? 'text-amber-300' : isDark ? 'text-amber-200' : 'text-amber-700')}>
                    Waiting for your response
                  </p>
                  <p className={cn('text-xs mt-1', textMuted)}>
                    Answer the question above to continue
                  </p>
                </div>
              </div>
            )}

            {hasError && error && (
              <div className={cn(
                'flex items-start gap-2 p-3 rounded-lg border',
                isBrandTheme ? 'bg-red-500/10 border-red-500/20' : 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800'
              )}>
                <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                <p className={cn('text-sm', isBrandTheme ? 'text-red-300' : 'text-red-600 dark:text-red-400')}>{error}</p>
              </div>
            )}

            {isDone && !hasError && (
              <div className={cn(
                'flex items-center gap-2 p-3 rounded-lg border',
                isBrandTheme ? 'bg-green-500/10 border-green-500/20' : 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800'
              )}>
                <CheckCircle2 className="w-4 h-4 text-green-500" />
                <p className={cn('text-sm', isBrandTheme ? 'text-green-300' : 'text-green-600 dark:text-green-400')}>
                  Task completed successfully!
                </p>
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Input */}
        <form onSubmit={handleSubmit} className={cn('p-4 border-t', borderColor)}>
          <div className="relative">
            <textarea
              ref={inputRef}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit(e);
                }
              }}
              placeholder={isAwaitingClarification ? "Answer the agent's question..." : "Describe what to change..."}
              disabled={isRunning && !isAwaitingClarification}
              className={cn(
                'w-full min-h-[80px] max-h-[160px] resize-none rounded-xl border px-4 py-3 pr-12 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50',
                inputBg,
                inputText
              )}
            />
            <button
              type="submit"
              disabled={!prompt.trim() || isRunning}
              className="absolute bottom-3 right-3 w-8 h-8 rounded-full flex items-center justify-center transition-all disabled:opacity-40"
              style={{ 
                background: prompt.trim() && !isRunning 
                  ? accentGradient 
                  : (isBrandTheme ? 'rgba(255,255,255,0.1)' : isDark ? '#374151' : '#e5e7eb')
              }}
            >
              {isRunning ? (
                <Loader2 className="w-4 h-4 text-white animate-spin" />
              ) : (
                <Send className={cn('w-4 h-4', prompt.trim() ? 'text-white' : textMuted)} />
              )}
            </button>
          </div>
          {isDone && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                reset();
                setLastFileCount(0);
              }}
              className={cn('mt-2', textSecondary, surfaceHover)}
            >
              Start new task
            </Button>
          )}
        </form>
      </div>

      {/* Sidebar Resize Handle */}
      <div
        className={cn(
          'w-1 cursor-col-resize flex-shrink-0 group relative',
          isResizingSidebar 
            ? 'bg-cyan-500' 
            : isBrandTheme ? 'bg-white/10 hover:bg-cyan-500/50' : isDark ? 'bg-gray-700 hover:bg-cyan-500/50' : 'bg-gray-200 hover:bg-cyan-500/50'
        )}
        onMouseDown={(e) => {
          e.preventDefault();
          setIsResizingSidebar(true);
        }}
      >
        {/* Visual indicator on hover */}
        <div className={cn(
          'absolute inset-y-0 -left-1 -right-1 opacity-0 group-hover:opacity-100 transition-opacity',
          isResizingSidebar && 'opacity-100'
        )} />
      </div>

      {/* Right Panel - Preview */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Preview Header with Sandbox Status */}
        <div className={cn('px-4 py-2 border-b flex items-center justify-between', borderColor, surfaceBg)}>
          <div className="flex items-center gap-2">
            <Eye className={cn('w-4 h-4', textSecondary)} />
            <span className={cn('text-sm', textSecondary)}>Live Preview</span>
            
            {/* Sandbox Status Badge */}
            <Badge 
              variant="outline" 
              className={cn(
                'text-xs flex items-center gap-1',
                sandboxLoading 
                  ? 'border-yellow-500/30 text-yellow-500'
                  : sandboxReady
                    ? 'border-green-500/30 text-green-500'
                    : sandboxNeedsInit
                      ? 'border-red-500/30 text-red-500'
                      : (isBrandTheme ? 'border-white/20 text-white/60' : isDark ? 'border-gray-600 text-gray-400' : 'border-gray-300 text-gray-500')
              )}
            >
              {sandboxLoading && <Loader2 className="w-3 h-3 animate-spin" />}
              {sandboxReady && !sandboxLoading && <HardDrive className="w-3 h-3" />}
              {sandboxNeedsInit && <Package className="w-3 h-3" />}
              {sandboxStatus === SANDBOX_STATUS.SYNCING && 'Syncing...'}
              {sandboxStatus === SANDBOX_STATUS.GENERATING && 'Generating...'}
              {sandboxStatus === SANDBOX_STATUS.INITIALIZING && 'Initializing...'}
              {sandboxStatus === SANDBOX_STATUS.CHECKING && 'Checking...'}
              {sandboxReady && !sandboxLoading && 'Sandbox'}
              {sandboxNeedsInit && 'No Sandbox'}
              {sandboxNeedsSync && 'Specs Stale'}
            </Badge>
            
            {/* Dev Server Status Badge */}
            <Badge 
              variant="outline" 
              className={cn(
                'text-xs flex items-center gap-1',
                devServerRunning 
                  ? 'border-green-500/30 text-green-500'
                  : devServerStarting
                    ? 'border-yellow-500/30 text-yellow-500'
                    : devServerError
                      ? 'border-red-500/30 text-red-500'
                      : (isBrandTheme ? 'border-white/20 text-white/60' : isDark ? 'border-gray-600 text-gray-400' : 'border-gray-300 text-gray-500')
              )}
            >
              {devServerStarting && <Loader2 className="w-3 h-3 animate-spin" />}
              {devServerRunning && <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />}
              {devServerRunning && `Dev :${devServerUrl?.split(':').pop() || ''}`}
              {devServerStarting && 'Starting...'}
              {!devServerRunning && !devServerStarting && 'Dev Stopped'}
            </Badge>
            
            {/* Branch Selector */}
            <div className="relative">
              <button
                onClick={() => {
                  setShowBranchMenu(!showBranchMenu);
                  if (!showBranchMenu) {
                    fetchBranches();
                  }
                }}
                className={cn(
                  'flex items-center gap-1.5 px-2 py-1 rounded text-xs transition-colors',
                  isBrandTheme 
                    ? 'bg-white/10 text-white/80 hover:bg-white/20' 
                    : isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                )}
                disabled={branchLoading}
              >
                <GitBranch className="w-3.5 h-3.5" />
                <span className="max-w-[100px] truncate">{currentBranch || 'main'}</span>
                {stashes?.length > 0 && (
                  <span className="flex items-center justify-center w-4 h-4 text-[10px] font-bold bg-amber-500 text-black rounded-full" title={`${stashes.length} stashed changes`}>
                    {stashes.length}
                  </span>
                )}
                <ChevronDown className={cn('w-3 h-3 transition-transform', showBranchMenu && 'rotate-180')} />
              </button>
              
              {/* Branch dropdown menu */}
              {showBranchMenu && (
                <div 
                  className={cn(
                    'absolute top-full right-0 mt-1 w-64 rounded-lg shadow-lg border z-50 overflow-hidden',
                    isBrandTheme 
                      ? 'bg-gray-900/95 border-white/10' 
                      : isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                  )}
                >
                  {/* New branch input */}
                  <div className={cn('p-2 border-b', isBrandTheme ? 'border-white/10' : isDark ? 'border-gray-700' : 'border-gray-200')}>
                    <form onSubmit={async (e) => {
                      e.preventDefault();
                      if (!newBranchName.trim()) return;
                      setBranchError(null);
                      try {
                        await createBranch(newBranchName.trim());
                        setNewBranchName('');
                        setShowBranchMenu(false);
                        setPreviewKey(k => k + 1);
                      } catch (err) {
                        setBranchError(err.message);
                      }
                    }}>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={newBranchName}
                          onChange={(e) => setNewBranchName(e.target.value)}
                          placeholder="New branch name..."
                          className={cn(
                            'flex-1 px-2 py-1 rounded text-xs border',
                            isBrandTheme 
                              ? 'bg-white/5 border-white/10 text-white placeholder:text-white/40' 
                              : isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'
                          )}
                        />
                        <Button
                          type="submit"
                          size="sm"
                          variant="ghost"
                          disabled={!newBranchName.trim() || branchLoading}
                          className="px-2 py-1 h-auto text-xs"
                        >
                          <Plus className="w-3 h-3" />
                        </Button>
                      </div>
                    </form>
                    {branchError && (
                      <p className="text-xs text-red-500 mt-1">{branchError}</p>
                    )}
                  </div>
                  
                  {/* Branch list */}
                  <div className="max-h-48 overflow-y-auto">
                    {branchLoading ? (
                      <div className="flex items-center justify-center py-3">
                        <Loader2 className="w-4 h-4 animate-spin text-cyan-500" />
                      </div>
                    ) : branches.length === 0 ? (
                      <p className={cn('text-xs text-center py-3', textMuted)}>No branches</p>
                    ) : (
                      branches.filter(b => !b.isRemote).map((branch) => (
                        <div
                          key={branch.name}
                          className={cn(
                            'flex items-center justify-between px-3 py-2 text-xs',
                            branch.isCurrent 
                              ? (isBrandTheme ? 'bg-cyan-500/20' : isDark ? 'bg-cyan-900/30' : 'bg-cyan-50')
                              : (isBrandTheme ? 'hover:bg-white/5' : isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50')
                          )}
                        >
                          <button
                            onClick={async () => {
                              if (branch.isCurrent) return;
                              setBranchError(null);
                              try {
                                const result = await switchBranch(branch.name);
                                setShowBranchMenu(false);
                                setPreviewKey(k => k + 1);
                                // Show notification if changes were stashed
                                if (result?.wasStashed) {
                                  alert('Your uncommitted changes were stashed. You can restore them using "Apply Stash" in the branch menu.');
                                }
                              } catch (err) {
                                setBranchError(err.message);
                              }
                            }}
                            className={cn(
                              'flex items-center gap-2 flex-1 text-left',
                              branch.isCurrent ? 'cursor-default' : 'cursor-pointer'
                            )}
                            disabled={branchLoading}
                          >
                            <GitBranch className={cn('w-3 h-3', branch.isCurrent && 'text-cyan-500')} />
                            <span className={cn(
                              'truncate',
                              branch.isCurrent ? 'font-medium text-cyan-500' : textSecondary
                            )}>
                              {branch.name}
                            </span>
                            {branch.isCurrent && (
                              <CheckCircle2 className="w-3 h-3 text-cyan-500 flex-shrink-0" />
                            )}
                          </button>
                          
                          {/* Branch actions */}
                          {!branch.isCurrent && (
                            <div className="flex items-center gap-1">
                              {/* Compare with current */}
                              <button
                                onClick={async () => {
                                  setBranchError(null);
                                  setSelectedBranchForCompare(branch.name);
                                  try {
                                    const result = await compareBranches(currentBranch, branch.name);
                                    setCompareResult(result);
                                    setShowBranchCompare(true);
                                  } catch (err) {
                                    setBranchError(err.message);
                                  }
                                }}
                                className={cn(
                                  'p-1 rounded',
                                  isBrandTheme ? 'hover:bg-white/10' : isDark ? 'hover:bg-gray-600' : 'hover:bg-gray-200'
                                )}
                                title={`Compare with ${currentBranch}`}
                              >
                                <Eye className="w-3 h-3 text-blue-500" />
                              </button>
                              {/* Pull from this branch */}
                              <button
                                onClick={async () => {
                                  setBranchError(null);
                                  const strategy = window.confirm(
                                    `Pull changes from "${branch.name}" into "${currentBranch}"?\n\nClick OK for normal merge, Cancel to choose squash merge.`
                                  ) ? 'merge' : (
                                    window.confirm('Use squash merge? (combines all commits into one)') ? 'squash' : null
                                  );
                                  
                                  if (!strategy) return;
                                  
                                  try {
                                    await pullFromBranch(branch.name, { strategy });
                                    setShowBranchMenu(false);
                                    setPreviewKey(k => k + 1);
                                  } catch (err) {
                                    setBranchError(err.message);
                                  }
                                }}
                                className={cn(
                                  'p-1 rounded',
                                  isBrandTheme ? 'hover:bg-white/10' : isDark ? 'hover:bg-gray-600' : 'hover:bg-gray-200'
                                )}
                                title={`Pull from ${branch.name} into ${currentBranch}`}
                              >
                                <GitMerge className="w-3 h-3 text-green-500" />
                              </button>
                              {/* Delete branch */}
                              {branch.name !== 'main' && branch.name !== 'master' && (
                                <button
                                  onClick={async () => {
                                    if (!confirm(`Delete branch "${branch.name}"?`)) return;
                                    setBranchError(null);
                                    try {
                                      await deleteBranch(branch.name);
                                    } catch (err) {
                                      setBranchError(err.message);
                                    }
                                  }}
                                  className={cn(
                                    'p-1 rounded',
                                    isBrandTheme ? 'hover:bg-white/10' : isDark ? 'hover:bg-gray-600' : 'hover:bg-gray-200'
                                  )}
                                  title="Delete branch"
                                >
                                  <Trash2 className="w-3 h-3 text-red-500" />
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                  
                  {/* Stashes section */}
                  {hasStashes && (
                    <div className={cn('border-t', isBrandTheme ? 'border-white/10' : isDark ? 'border-gray-700' : 'border-gray-200')}>
                      <div className={cn('px-3 py-2 text-xs font-medium', textMuted)}>
                        Stashed Changes ({stashes.length})
                      </div>
                      {stashes.map((stash, idx) => (
                        <div
                          key={stash.ref}
                          className={cn(
                            'flex items-center justify-between px-3 py-2 text-xs',
                            isBrandTheme ? 'hover:bg-white/5' : isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                          )}
                        >
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <Package className="w-3 h-3 text-amber-500 flex-shrink-0" />
                            <span className={cn('truncate', textSecondary)}>
                              {stash.message}
                            </span>
                          </div>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={async () => {
                                setBranchError(null);
                                try {
                                  await applyStash(stash.ref);
                                  setPreviewKey(k => k + 1);
                                } catch (err) {
                                  setBranchError(err.message);
                                }
                              }}
                              className={cn(
                                'p-1 rounded text-xs',
                                isBrandTheme ? 'hover:bg-white/10' : isDark ? 'hover:bg-gray-600' : 'hover:bg-gray-200'
                              )}
                              title="Apply stash (keep)"
                            >
                              <Eye className="w-3 h-3 text-blue-500" />
                            </button>
                            <button
                              onClick={async () => {
                                setBranchError(null);
                                try {
                                  await popStash();
                                  setPreviewKey(k => k + 1);
                                } catch (err) {
                                  setBranchError(err.message);
                                }
                              }}
                              className={cn(
                                'p-1 rounded text-xs',
                                isBrandTheme ? 'hover:bg-white/10' : isDark ? 'hover:bg-gray-600' : 'hover:bg-gray-200'
                              )}
                              title="Pop stash (apply and remove)"
                            >
                              <CheckCircle2 className="w-3 h-3 text-green-500" />
                            </button>
                            <button
                              onClick={async () => {
                                if (!confirm('Drop this stash? This cannot be undone.')) return;
                                setBranchError(null);
                                try {
                                  await dropStash(stash.ref);
                                } catch (err) {
                                  setBranchError(err.message);
                                }
                              }}
                              className={cn(
                                'p-1 rounded text-xs',
                                isBrandTheme ? 'hover:bg-white/10' : isDark ? 'hover:bg-gray-600' : 'hover:bg-gray-200'
                              )}
                              title="Drop stash"
                            >
                              <Trash2 className="w-3 h-3 text-red-500" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              {/* Branch comparison modal */}
              {showBranchCompare && compareResult && (
                <div 
                  className={cn(
                    'absolute top-full right-0 mt-1 w-80 rounded-lg shadow-lg border z-50 overflow-hidden',
                    isBrandTheme 
                      ? 'bg-gray-900/95 border-white/10' 
                      : isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                  )}
                >
                  <div className={cn('p-3 border-b flex items-center justify-between', isBrandTheme ? 'border-white/10' : isDark ? 'border-gray-700' : 'border-gray-200')}>
                    <h4 className={cn('text-sm font-medium', textPrimary)}>
                      Compare: {currentBranch} ← {selectedBranchForCompare}
                    </h4>
                    <button
                      onClick={() => {
                        setShowBranchCompare(false);
                        setCompareResult(null);
                      }}
                      className={cn('p-1 rounded', surfaceHover)}
                    >
                      <ChevronDown className="w-4 h-4" />
                    </button>
                  </div>
                  
                  <div className="p-3 space-y-3">
                    {/* Ahead/Behind stats */}
                    <div className="flex gap-4 text-xs">
                      <div className={cn('flex items-center gap-1', compareResult.ahead > 0 ? 'text-green-500' : textMuted)}>
                        <span className="font-medium">{compareResult.ahead}</span> ahead
                      </div>
                      <div className={cn('flex items-center gap-1', compareResult.behind > 0 ? 'text-amber-500' : textMuted)}>
                        <span className="font-medium">{compareResult.behind}</span> behind
                      </div>
                    </div>
                    
                    {/* Files changed */}
                    {compareResult.filesChanged?.length > 0 && (
                      <div>
                        <p className={cn('text-xs mb-2', textMuted)}>
                          {compareResult.filesChanged.length} files changed
                        </p>
                        <div className="max-h-32 overflow-y-auto space-y-1">
                          {compareResult.filesChanged.slice(0, 10).map((file, idx) => (
                            <div key={idx} className="flex items-center gap-2 text-xs">
                              <span className={cn(
                                'w-4 text-center font-mono',
                                file.status === 'A' ? 'text-green-500' :
                                file.status === 'D' ? 'text-red-500' :
                                file.status === 'M' ? 'text-amber-500' : textMuted
                              )}>
                                {file.status}
                              </span>
                              <span className={cn('truncate', textSecondary)}>{file.path}</span>
                            </div>
                          ))}
                          {compareResult.filesChanged.length > 10 && (
                            <p className={cn('text-xs', textMuted)}>
                              +{compareResult.filesChanged.length - 10} more files
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Actions */}
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={async () => {
                          setBranchError(null);
                          try {
                            await pullFromBranch(selectedBranchForCompare);
                            setShowBranchCompare(false);
                            setShowBranchMenu(false);
                            setPreviewKey(k => k + 1);
                          } catch (err) {
                            setBranchError(err.message);
                          }
                        }}
                        className="flex-1 text-xs"
                      >
                        <GitMerge className="w-3 h-3 mr-1" />
                        Merge
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setShowBranchCompare(false);
                          setCompareResult(null);
                        }}
                        className="text-xs"
                      >
                        Close
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Sandbox Controls */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowSandboxPanel(!showSandboxPanel)}
              className={cn(textSecondary, surfaceHover)}
              title="Sandbox settings"
            >
              <Settings className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRefreshPreview}
              className={cn(textSecondary, surfaceHover)}
            >
              <RefreshCw className="w-4 h-4 mr-1" />
              Refresh
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => window.open(effectivePreviewUrl, '_blank')}
              className={cn(textSecondary, surfaceHover)}
            >
              <ExternalLink className="w-4 h-4 mr-1" />
              Open
            </Button>
          </div>
        </div>

        {/* Sandbox Control Panel (collapsible) */}
        {showSandboxPanel && (
          <div className={cn('px-4 py-3 border-b space-y-3', borderColor, surfaceBg)}>
            <div className="flex items-center justify-between">
              <h3 className={cn('text-sm font-medium', textPrimary)}>Sandbox Controls</h3>
              <Badge variant="outline" className={cn('text-xs', textMuted)}>
                {brandSlug || 'No brand selected'}
              </Badge>
            </div>
            
            {sandboxError && (
              <div className="flex items-center gap-2 p-2 rounded bg-red-500/10 border border-red-500/20">
                <AlertCircle className="w-4 h-4 text-red-500" />
                <span className="text-sm text-red-500">{sandboxError}</span>
              </div>
            )}
            
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => syncSpecs()}
                disabled={sandboxLoading || !brandId}
                className={cn(
                  'flex items-center gap-2',
                  isBrandTheme ? 'border-white/20 text-white hover:bg-white/10' : ''
                )}
              >
                <FolderSync className="w-4 h-4" />
                Sync Specs
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => regenerate()}
                disabled={sandboxLoading || !brandId}
                className={cn(
                  'flex items-center gap-2',
                  isBrandTheme ? 'border-white/20 text-white hover:bg-white/10' : ''
                )}
              >
                <Wand2 className="w-4 h-4" />
                Regenerate Site
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (confirm('This will delete and recreate the sandbox. Are you sure?')) {
                    resetSandbox();
                  }
                }}
                disabled={sandboxLoading || !brandId}
                className={cn(
                  'flex items-center gap-2 text-red-500 border-red-500/30 hover:bg-red-500/10',
                )}
              >
                <Trash2 className="w-4 h-4" />
                Reset
              </Button>
            </div>
            
            {sandboxInfo && (
              <div className={cn('text-xs space-y-1', textMuted)}>
                <div className="flex items-center gap-2">
                  <Database className="w-3 h-3" />
                  <span>Storage: {sandboxInfo.storageType || 'local'}</span>
                </div>
                {sandboxInfo.sandboxRoot && (
                  <div className="flex items-center gap-2">
                    <HardDrive className="w-3 h-3" />
                    <span className="truncate">{sandboxInfo.sandboxRoot}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Preview Frame */}
        <div className="flex-1 relative bg-white">
          {sandboxNeedsInit ? (
            <div className={cn('w-full h-full flex items-center justify-center', isDark ? 'bg-gray-900' : 'bg-gray-100')}>
              <div className="text-center p-8">
                <Package className={cn('w-16 h-16 mx-auto mb-4', textMuted)} />
                <h3 className={cn('text-lg font-medium mb-2', textPrimary)}>Sandbox Not Initialized</h3>
                <p className={cn('text-sm mb-4', textMuted)}>
                  Setting up sandbox for {brandName || brandSlug}...
                </p>
                {sandboxLoading ? (
                  <Loader2 className="w-6 h-6 mx-auto animate-spin text-cyan-500" />
                ) : (
                  <Button
                    onClick={() => resetSandbox()}
                    className="bg-cyan-600 hover:bg-cyan-700 text-white"
                  >
                    Initialize Sandbox
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <>
              {/* Preview URL status bar */}
              <div className={cn(
                'absolute bottom-0 left-0 right-0 z-10 px-3 py-1 text-xs flex items-center gap-2',
                isBrandTheme ? 'bg-black/60 text-white/70' : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
              )}>
                <span className={cn(
                  'w-2 h-2 rounded-full',
                  devServerRunning ? 'bg-green-500' : 'bg-yellow-500'
                )} />
                <span className="truncate flex-1">
                  {devServerRunning ? 'Dev Server' : 'Static Preview'}: {effectivePreviewUrl}
                </span>
                {stagedFiles.length > 0 && (
                  <Badge variant="outline" className="text-xs border-amber-500/50 text-amber-500">
                    {stagedFiles.length} staged
                  </Badge>
                )}
              </div>
              
              <iframe
                key={previewKey}
                src={effectivePreviewUrl}
                className="w-full h-full border-0 pb-6"
                title="Site Preview"
              />
              
              {/* Overlay for changed files indicator */}
              {changedFiles.length > 0 && (
                <div className="absolute top-2 right-2 z-10">
                  <div className="bg-green-500 text-white text-xs px-2 py-1 rounded-full shadow-lg animate-pulse">
                    {changedFiles.length} file{changedFiles.length > 1 ? 's' : ''} updated
                  </div>
                </div>
              )}
              
              {/* Sandbox loading overlay */}
              {sandboxLoading && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-20">
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-xl text-center">
                    <Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin text-cyan-500" />
                    <p className={cn('text-sm', isDark ? 'text-gray-300' : 'text-gray-600')}>
                      {sandboxStatus === SANDBOX_STATUS.SYNCING && 'Syncing specs...'}
                      {sandboxStatus === SANDBOX_STATUS.GENERATING && 'Generating site...'}
                      {sandboxStatus === SANDBOX_STATUS.INITIALIZING && 'Initializing sandbox...'}
                      {sandboxStatus === SANDBOX_STATUS.CHECKING && 'Checking sandbox...'}
                    </p>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Console Resize Handle */}
        <div
          className={cn(
            'h-1 cursor-row-resize flex-shrink-0 group relative',
            isResizingConsole 
              ? 'bg-cyan-500' 
              : isBrandTheme ? 'bg-white/10 hover:bg-cyan-500/50' : isDark ? 'bg-gray-700 hover:bg-cyan-500/50' : 'bg-gray-200 hover:bg-cyan-500/50'
          )}
          onMouseDown={(e) => {
            e.preventDefault();
            setIsResizingConsole(true);
          }}
        >
          {/* Visual indicator on hover */}
          <div className={cn(
            'absolute inset-x-0 -top-1 -bottom-1 opacity-0 group-hover:opacity-100 transition-opacity',
            isResizingConsole && 'opacity-100'
          )} />
        </div>

        {/* Bottom Panel - Tabs for Pending / Changes / History */}
        <div 
          className={cn('border-t flex flex-col flex-shrink-0', borderColor, surfaceBg)}
          style={{ height: consolePanelHeight }}
        >
          {/* Tab Headers */}
          <div className={cn('flex border-b flex-shrink-0', borderColor)}>
            <button
              onClick={() => setActiveTab('pending')}
              className={cn(
                'flex items-center gap-2 px-4 py-2 text-sm transition-colors',
                activeTab === 'pending' ? tabActive : tabInactive
              )}
            >
              <GitPullRequest className="w-4 h-4" />
              Staged
              {(stagedCount > 0 || pendingCount > 0) && (
                <Badge className="ml-1 h-5 px-1.5 bg-amber-500/20 text-amber-500 border-amber-500/30">
                  {stagedCount + pendingCount}
                </Badge>
              )}
            </button>
            <button
              onClick={() => setActiveTab('changes')}
              className={cn(
                'flex items-center gap-2 px-4 py-2 text-sm transition-colors',
                activeTab === 'changes' ? tabActive : tabInactive
              )}
            >
              <FileCode className="w-4 h-4" />
              Applied ({changedFiles.length})
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={cn(
                'flex items-center gap-2 px-4 py-2 text-sm transition-colors',
                activeTab === 'history' ? tabActive : tabInactive
              )}
            >
              <History className="w-4 h-4" />
              History
            </button>
          </div>

          {/* Tab Content */}
          <div className="flex-1 overflow-auto">
            {activeTab === 'pending' && (
              <div className="p-3">
                {/* Staged files - written but not committed */}
                {stagedFiles && stagedFiles.length > 0 && (
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className={cn('text-sm font-medium', textSecondary)}>
                        Staged Changes ({stagedFiles.length})
                      </h3>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => discardAll()}
                          className={cn(
                            'text-xs text-red-500 border-red-500/30 hover:bg-red-500/10',
                          )}
                        >
                          <Trash2 className="w-3 h-3 mr-1" />
                          Discard All
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => commitAll('User approved agent changes')}
                          className={cn(
                            'text-xs',
                            isBrandTheme 
                              ? 'bg-green-600 hover:bg-green-700 text-white' 
                              : 'bg-green-600 hover:bg-green-700 text-white'
                          )}
                        >
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          Commit All
                        </Button>
                      </div>
                    </div>
                    <p className={cn('text-xs mb-3', textMuted)}>
                      These changes are visible in the preview. Commit to save permanently, or discard to revert.
                    </p>
                    <div className="space-y-1">
                      {stagedFiles.map((file, idx) => (
                        <StagedFileRow
                          key={idx}
                          file={file}
                          brandId={brandId}
                          isBrandTheme={isBrandTheme}
                          isDark={isDark}
                          surfaceBg={surfaceBg}
                          textSecondary={textSecondary}
                          textMuted={textMuted}
                          onCommitFile={commitFile}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* No staged changes message */}
                {(!stagedFiles || stagedFiles.length === 0) && (!pendingChanges || pendingChanges.length === 0) && (
                  <div className={cn('text-center py-8', textMuted)}>
                    <GitCommit className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No staged changes</p>
                    <p className="text-xs mt-1">Changes will appear here when the agent modifies files</p>
                  </div>
                )}

                {/* Legacy pending changes panel (for preview mode fallback) */}
                {pendingChanges && pendingChanges.length > 0 && (
                  <PendingChangesPanel
                    changes={pendingChanges}
                    onAcceptChange={acceptChange}
                    onRejectChange={rejectChange}
                    onAcceptAll={acceptAll}
                    onRejectAll={rejectAll}
                    isBrandTheme={isBrandTheme}
                    isDark={isDark}
                  />
                )}
              </div>
            )}

            {activeTab === 'changes' && (
              <div className="p-3 space-y-1">
                {changedFiles.length === 0 ? (
                  <p className={cn('text-sm text-center py-4', textMuted)}>
                    No files applied yet
                  </p>
                ) : (
                  changedFiles.map((file, idx) => (
                    <div
                      key={idx}
                      className={cn('flex items-center gap-2 px-3 py-1.5 rounded text-sm', surfaceBg)}
                    >
                      <Badge 
                        variant="outline" 
                        className="text-xs border-green-500/30 text-green-500"
                      >
                        {file.action}
                      </Badge>
                      <code className={cn('text-xs truncate flex-1', textSecondary)}>
                        {file.path}
                      </code>
                    </div>
                  ))
                )}
              </div>
            )}

            {activeTab === 'history' && (
              <div className="p-3 space-y-2">
                {historyLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-5 h-5 animate-spin text-cyan-500" />
                  </div>
                ) : historyError ? (
                  <p className={cn('text-sm text-center py-4 text-red-500')}>
                    {historyError}
                  </p>
                ) : commits.length === 0 ? (
                  <p className={cn('text-sm text-center py-4', textMuted)}>
                    No commit history yet
                  </p>
                ) : (
                  commits.map((commit) => (
                    <CommitCard
                      key={commit.hash}
                      commit={commit}
                      onRevert={handleRevert}
                      isBrandTheme={isBrandTheme}
                      isDark={isDark}
                      cardBg={cardBg}
                      textSecondary={textSecondary}
                      textMuted={textMuted}
                      surfaceHover={surfaceHover}
                    />
                  ))
                )}
                {commits.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => fetchHistory(50)}
                    className={cn('w-full', textSecondary, surfaceHover)}
                  >
                    Load more
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Commit card showing commit info with revert button
 */
const CommitCard = ({ commit, onRevert, isBrandTheme, isDark, cardBg, textSecondary, textMuted, surfaceHover }) => {
  const [reverting, setReverting] = useState(false);
  const isAgentCommit = commit.message.includes('[session-') || commit.message.startsWith('Agent');

  const handleRevert = async () => {
    setReverting(true);
    try {
      await onRevert(commit.hash);
    } finally {
      setReverting(false);
    }
  };

  const formatTime = (dateInput) => {
    if (!dateInput) return '';
    const date = new Date(dateInput);
    if (isNaN(date.getTime())) return 'Invalid Date';

    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className={cn('flex items-start gap-3 p-2 rounded-lg', surfaceHover)}>
      <div className={cn(
        'w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5',
        isAgentCommit 
          ? (isBrandTheme ? 'bg-cyan-500/20' : isDark ? 'bg-cyan-900/30' : 'bg-cyan-100')
          : (isBrandTheme ? 'bg-white/10' : isDark ? 'bg-gray-700' : 'bg-gray-200')
      )}>
        {isAgentCommit ? (
          <Sparkles className="w-3 h-3 text-cyan-500" />
        ) : (
          <GitCommit className={cn('w-3 h-3', textMuted)} />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className={cn('text-sm truncate', textSecondary)}>
          {commit.message}
        </p>
        <div className="flex items-center gap-2 mt-1">
          <code className={cn('text-xs', textMuted)}>{commit.shortHash}</code>
          <span className={cn('text-xs flex items-center gap-1', textMuted)}>
            <Clock className="w-3 h-3" />
            {formatTime(commit.date || commit.timestamp)}
          </span>
        </div>
      </div>
      <Button
        variant="ghost"
        size="sm"
        onClick={handleRevert}
        disabled={reverting}
        className={cn('flex-shrink-0', textMuted, surfaceHover)}
        title="Revert this commit"
      >
        {reverting ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <RotateCcw className="w-4 h-4" />
        )}
      </Button>
    </div>
  );
};

/**
 * Individual step card showing thought, action, and result
 */
const StepCard = ({ step, isBrandTheme, isDark, cardBg, textPrimary, textSecondary, textMuted, borderColor }) => {
  const [expanded, setExpanded] = useState(false);
  
  const statusIcon = {
    thinking: <Loader2 className="w-3.5 h-3.5 text-cyan-500 animate-spin" />,
    executing: <Loader2 className="w-3.5 h-3.5 text-yellow-500 animate-spin" />,
    complete: <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />,
    error: <AlertCircle className="w-3.5 h-3.5 text-red-500" />,
  }[step.status] || null;

  const surfaceHover = isBrandTheme 
    ? 'hover:bg-white/10' 
    : isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50';

  const codeBg = isBrandTheme 
    ? 'bg-black/30' 
    : isDark ? 'bg-gray-900' : 'bg-gray-100';

  return (
    <Card className={cn('overflow-hidden border', cardBg)}>
      <button
        onClick={() => setExpanded(!expanded)}
        className={cn('w-full px-3 py-2 flex items-start gap-2 text-left', surfaceHover)}
      >
        <div className="mt-0.5">{statusIcon}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={cn('text-xs', textMuted)}>Step {step.number}</span>
            {step.action && typeof step.action === 'object' && (
              <Badge 
                variant="outline" 
                className={cn(
                  'text-xs',
                  isBrandTheme ? 'border-white/20 text-white/60' : isDark ? 'border-gray-600 text-gray-400' : 'border-gray-300 text-gray-500'
                )}
              >
                {step.action.action}
              </Badge>
            )}
          </div>
          <p className={cn('text-sm mt-0.5 line-clamp-2', textSecondary)}>
            {step.thought}
          </p>
        </div>
        {expanded ? (
          <ChevronDown className={cn('w-4 h-4 flex-shrink-0', textMuted)} />
        ) : (
          <ChevronRight className={cn('w-4 h-4 flex-shrink-0', textMuted)} />
        )}
      </button>

      {expanded && (
        <div className={cn('px-3 pb-3 space-y-2 border-t mt-1 pt-2 grid grid-cols-1', borderColor)}>
          {/* Full thought when expanded */}
          <div className="mb-2 min-w-0">
            <span className={cn('text-xs uppercase tracking-wide block mb-1', textMuted)}>Thought</span>
            <p className={cn('text-sm whitespace-pre-wrap break-words', textSecondary)}>{step.thought}</p>
          </div>

          {step.action && (
            <div className="min-w-0">
              <span className={cn('text-xs uppercase tracking-wide', textMuted)}>Action</span>
              <pre className={cn('mt-1 p-2 rounded text-xs overflow-x-auto w-full whitespace-pre', codeBg, 'text-cyan-500')}>
                {typeof step.action === 'object' 
                  ? JSON.stringify(step.action, null, 2)
                  : step.action}
              </pre>
            </div>
          )}
          {step.result && (
            <div className="min-w-0">
              <span className={cn('text-xs uppercase tracking-wide', textMuted)}>Result</span>
              <pre className={cn('mt-1 p-2 rounded text-xs overflow-x-auto w-full max-h-60 whitespace-pre', codeBg, 'text-green-500')}>
                {typeof step.result === 'object'
                  ? JSON.stringify(step.result, null, 2)
                  : step.result}
              </pre>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

export default SiteAgentView;
